import type { ReactNode } from 'react'
import { AlertTriangle, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'

export function Spinner({ className }: { className?: string }) {
  return (
    <span role="status" aria-live="polite" className={cn('inline-flex items-center', className)}>
      <Loader2 className="text-blood size-5 animate-spin" aria-hidden />
      <span className="sr-only">Loading</span>
    </span>
  )
}

/** Full-section loading state. Deliberately quiet — no spinner carnival. */
export function LoadingBlock({
  label = 'Loading',
  className,
}: {
  label?: string
  className?: string
}) {
  return (
    <div className={cn('text-bone-faint flex items-center justify-center gap-3 py-20', className)}>
      <Loader2 className="text-blood size-4 animate-spin" aria-hidden />
      <span className="eyebrow">{label}</span>
    </div>
  )
}

/**
 * Shimmering placeholder. Sized by the caller so a grid holds its shape while
 * data arrives, instead of collapsing and then jolting the page down.
 */
export function Skeleton({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        'bg-ink-raised relative overflow-hidden',
        'after:via-bone/5 after:absolute after:inset-0 after:-translate-x-full after:animate-[marquee_2s_infinite] after:bg-gradient-to-r after:from-transparent after:to-transparent',
        className
      )}
    />
  )
}

interface StateProps {
  title: string
  description?: string
  icon?: ReactNode
  action?: ReactNode
  className?: string
}

export function EmptyState({ title, description, icon, action, className }: StateProps) {
  return (
    <div
      className={cn(
        'border-hairline flex flex-col items-center justify-center border border-dashed px-6 py-16 text-center',
        className
      )}
    >
      {icon ? <div className="text-bone-faint mb-4">{icon}</div> : null}
      <p className="condensed text-bone text-lg">{title}</p>
      {description ? (
        <p className="text-bone-faint mt-2 max-w-sm text-sm leading-relaxed">{description}</p>
      ) : null}
      {action ? <div className="mt-6">{action}</div> : null}
    </div>
  )
}

export function ErrorState({
  title = 'That did not load',
  description = 'Refresh the page, or try again in a moment.',
  action,
  className,
}: Partial<StateProps>) {
  return (
    <div
      className={cn(
        'border-blood/30 bg-blood/5 flex flex-col items-center justify-center border px-6 py-16 text-center',
        className
      )}
    >
      <AlertTriangle className="text-blood mb-4 size-6" aria-hidden />
      <p className="condensed text-bone text-lg">{title}</p>
      <p className="text-bone-dim mt-2 max-w-sm text-sm leading-relaxed">{description}</p>
      {action ? <div className="mt-6">{action}</div> : null}
    </div>
  )
}
